import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useJourney } from "@/contexts/JourneyContext";
import PostLoginEnrichment from "@/components/PostLoginEnrichment";
import axios from "axios";

/**
 * Welcome page flow:
 *
 *  Phase "checking":
 *    → Call GET /api/enrichment/status/
 *    → If PAN is already stored (enrichment was done before), skip the
 *      enrichment screen entirely: log the WELCOME event and navigate.
 *    → If PAN is NOT stored (first-time user), show splash → enrichment.
 *
 *  Phase "splash":
 *    → Show "Welcome to Intellend" for ~3 s, then switch to enrichment.
 *
 *  Phase "enriching":
 *    → PostLoginEnrichment calls POST /api/enrichment/run/ (backend skips
 *      any API whose data is already in DB), then calls onComplete.
 *
 *  Phase "navigating":
 *    → Keeps enrichment screen mounted while logEvent + refreshJourney run,
 *      then navigate() fires — eliminates blank-page flash.
 */

type Phase = "checking" | "splash" | "enriching" | "navigating";

export default function WelcomePage() {
  const navigate = useNavigate();
  const { refresh: refreshJourney, logEvent } = useJourney();

  const [phase, setPhase] = useState<Phase>("checking");

  // ── Helper: log WELCOME event and navigate to next page ─────────────────
  const advanceAndNavigate = async () => {
    try {
      const response = await logEvent({
        stage: "WELCOME",
        route: "/welcome",
        event_type: "page_view",
        advance: true,
      });
      await refreshJourney();
      const nextPage = response?.next_page_url;
      navigate(nextPage || "/requirements");
    } catch (err) {
      console.error("Failed to log journey event:", err);
      navigate("/requirements");
    }
  };

  // ── Phase 0: check enrichment status ────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const checkEnrichment = async () => {
      try {
        const { data } = await axios.get("/api/enrichment/status/", {
          withCredentials: true,
        });

        if (cancelled) return;

        // Skip the enrichment screen only when BOTH PAN and PAN Details Plus
        // are already stored. If PAN is stored but PAN Details Plus hasn't run
        // yet (e.g. first login where mobile_prefill stored PAN but enrichment
        // was skipped), we still need to call /api/enrichment/run/ so that
        // pan_details_plus_response is populated for the PAN-Aadhaar gate.
        if (data?.pan && data?.has_pan_details_plus) {
          setPhase("navigating");
          await advanceAndNavigate();
          return;
        }
      } catch (err) {
        // If the status check fails (network error, 401, etc.) fall through
        // to the normal enrichment flow so the user isn't stuck.
        console.warn("enrichment status check failed, proceeding normally:", err);
      }

      if (!cancelled) {
        // First-time user — show splash then enrichment loader
        setPhase("splash");
      }
    };

    checkEnrichment();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Phase 1: splash timer ────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "splash") return;
    const timer = setTimeout(() => setPhase("enriching"), 3000);
    return () => clearTimeout(timer);
  }, [phase]);

  // ── Phase 2: called by PostLoginEnrichment once all APIs are done ────────
  // Returns a Promise so PostLoginEnrichment keeps its screen mounted until
  // navigate() fires — this eliminates the blank-page flash.
  const handleEnrichmentComplete = async (): Promise<void> => {
    setPhase("navigating");
    await advanceAndNavigate();
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (phase === "checking" || phase === "navigating") {
    // Minimal loading state — show nothing (or a spinner) while we decide
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <svg
          className="animate-spin h-8 w-8 text-intellend-green"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
      </div>
    );
  }

  if (phase === "enriching") {
    return <PostLoginEnrichment onComplete={handleEnrichmentComplete} />;
  }

  // Splash screen (phase === "splash")
  return (
    <div className="flex items-center justify-center h-screen bg-white">
      <motion.h1
        initial={{ color: "rgb(6, 90, 78)", opacity: 0, y: 20, filter: "blur(10px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ duration: 1, ease: "easeOut" }}
        className="text-4xl font-bold drop-shadow-lg"
      >
        Welcome to Intellend
      </motion.h1>
    </div>
  );
}